import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardMedia, CardContent, Box, Button
} from '@mui/material';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import { getImageUrl } from '../utils/imageUtils';
import { getProductPath } from '../utils/productSlug';
import { useGifts } from '../gifts/GiftContext';
import { computeProductPricing } from '../utils/productPricing';
import CardOverlayPills from './CardOverlayPills';
import { CARD_HOVER_IMAGE_MARKERS } from '../config/productAssetConfig';

const SNAKE_COLOR = 'rgb(250, 250, 250)';
const CARD_RADIUS = 15;
const STROKE_WIDTH = 2;

function collectAlternateImages(product, primaryUrl) {
  const candidates = [];
  const extras = product?.extraImages || product?.extraimages || [];
  if (Array.isArray(extras)) {
    candidates.push(...extras);
  }

  const colors = Array.isArray(product?.colors) ? product.colors : [];
  colors.forEach((color) => {
    if (color?.mainImage) candidates.push(color.mainImage);
    const colorExtras = color?.extraImages || color?.extraimages || [];
    if (Array.isArray(colorExtras)) candidates.push(...colorExtras);
  });

  const primary = primaryUrl ? String(primaryUrl).trim() : '';
  return candidates
    .filter((url) => typeof url === 'string' && url.trim())
    .map((url) => getImageUrl(url.trim()))
    .filter((url) => url && url !== primary && url !== getImageUrl(primary));
}

/**
 * Prefer alternate images by asset URL markers (not product names).
 * Renaming a product does not change hover selection.
 */
function pickHoverImage(product, primaryUrl) {
  const alts = collectAlternateImages(product, primaryUrl);
  if (alts.length === 0) return null;

  for (const marker of CARD_HOVER_IMAGE_MARKERS) {
    const found = alts.find((url) =>
      url.toLowerCase().includes(String(marker).toLowerCase())
    );
    if (found) return found;
  }

  return alts[0];
}

export default function ProductCard({ product, onAddToCart, allProducts = null }) {
  const wrapRef = useRef(null);
  const navigate = useNavigate();
  const { language, isHebrew } = useLanguage();
  const t = translations[language];
  const { promotionForProduct } = useGifts();
  const [size, setSize] = useState({ w: 0, h: 0 });

  // Get the appropriate name and description based on language
  const productName = isHebrew ? product.name_he : product.name_en;
  const primaryImage = getImageUrl(product.homepageImage || product.homepageimage);
  const hoverImage = useMemo(() => pickHoverImage(product, primaryImage), [product, primaryImage]);
  const pricing = computeProductPricing(product);
  const giftPromo = promotionForProduct(product?.id);
  const hasGift =
    !!giftPromo &&
    ((giftPromo.books || []).length > 0 || (giftPromo.bookIds || []).length > 0);

  // Get default color (first color in the array, or null if no colors)
  const getDefaultColor = () => {
    if (product.colors && Array.isArray(product.colors) && product.colors.length > 0) {
      return product.colors[0]; // First color is default
    }
    return null;
  };

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0) scale(1)';
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    observer.observe(el);

    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ w: width, h: height });
    });
    ro.observe(el);

    return () => {
      observer.disconnect();
      ro.disconnect();
    };
  }, []);

  const handleCardClick = () => {
    navigate(getProductPath(product, allProducts));
  };

  const handleLearnMoreClick = (e) => {
    e.stopPropagation(); // Prevent card click when clicking Learn More
    navigate(getProductPath(product, allProducts));
  };

  // Stroke is centered on the path — place path so the stroke sits on the card edge
  const inset = STROKE_WIDTH / 2;
  const rectW = Math.max(0, size.w - STROKE_WIDTH);
  const rectH = Math.max(0, size.h - STROKE_WIDTH);
  const rectRx = Math.max(0, CARD_RADIUS - inset);

  return (
    <Box
      ref={wrapRef}
      onClick={handleCardClick}
      sx={{
        height: { xs: '28vh', sm: '40vh', md: '70vh' },
        width: { xs: '38vh', sm: '40vh', md: '70vh' },
        position: 'relative',
        borderRadius: `${CARD_RADIUS}px`,
        margin: { xs: '0px 0px 0px 0px', md: '20px 0px 20px 0px' },
        cursor: 'pointer',
        opacity: 0,
        transform: 'translateY(50px) scale(0.95)',
        transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
        // Allow snake stroke to sit on the outer edge without being clipped
        overflow: 'visible',
        '& .product-snake-path': {
          strokeDasharray: 1,
          strokeDashoffset: 1,
          // Hide completely when idle — round stroke caps leave a tiny dot otherwise
          opacity: 0,
          transition: 'stroke-dashoffset 1.15s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.01s linear 1.15s'
        },
        '&:hover .product-snake-path': {
          strokeDashoffset: 0,
          opacity: 1,
          transition: 'stroke-dashoffset 1.15s cubic-bezier(0.22, 1, 0.36, 1), opacity 0s linear'
        },
        // Desktop / fine pointer only — no zoom or image swap on mobile
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover .product-card-image-primary': {
            transform: 'scale(1.06)',
            opacity: hoverImage ? 0 : 1
          },
          '&:hover .product-card-image-hover': {
            transform: 'scale(1.06)',
            opacity: 1
          }
        }
      }}
    >
      <Card
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          borderRadius: `${CARD_RADIUS}px`,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          border: `${STROKE_WIDTH}px solid rgba(199, 61, 34, 1)`,
          boxSizing: 'border-box'
        }}
      >
        {/* Add to Cart Button - Top Left Corner */}
        {onAddToCart && (
          <Button
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              if (product.quantity > 0) {
                onAddToCart(product, getDefaultColor());
              }
            }}
            disabled={!product || product.quantity <= 0}
            sx={{
              position: 'absolute',
              top: { xs: 14, sm: 18, md: 18 },
              left: { xs: 14, sm: 18, md: 18 },
              zIndex: 10,
              backgroundColor: 'rgba(199, 61, 34, 1)',
              color: 'rgb(245, 240, 227)',
              px: 1.5,
              py: 0.60,
              fontSize: { xs: '0.8rem', md: '1.5rem' },
              fontWeight: 300,
              borderRadius: 1.5,
              minWidth: 'auto',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'rgb(245, 240, 227)',
                color: 'rgba(199, 61, 34, 1)',
                boxShadow: 'none',
                transition: 'background-color 0.3s ease'
              },
              '&:disabled': {
                backgroundColor: '#ccc',
                color: '#999',
                cursor: 'not-allowed'
              }
            }}
          >
            {product && product.quantity > 0 ?
              t.addToCart :
              (isHebrew ? 'בקרוב' : 'Coming Soon')
            }
          </Button>
        )}

        {hasGift ? (
          <Box
            aria-label={t.giftFreeChip || 'Gift'}
            sx={{
              position: 'absolute',
              top: { xs: 12, sm: 14, md: 16 },
              right: { xs: 12, sm: 14, md: 16 },
              zIndex: 10,
              width: { xs: 32, sm: 36, md: 42 },
              height: { xs: 32, sm: 36, md: 42 },
              borderRadius: '50%',
              backgroundColor: '#d8472a',
              color: '#f5f0e3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
              pointerEvents: 'none'
            }}
          >
            <CardGiftcardIcon sx={{ fontSize: { xs: 18, sm: 20, md: 24 } }} />
          </Box>
        ) : null}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden'
          }}
        >
          <CardMedia
            component="img"
            className="product-card-image-primary"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: 'scale(1)',
              transformOrigin: 'center center',
              opacity: 1,
              transition: 'transform 0.45s ease, opacity 0.35s ease'
            }}
            image={primaryImage}
            alt={productName}
          />
          {hoverImage && (
            <CardMedia
              component="img"
              className="product-card-image-hover"
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'scale(1)',
                transformOrigin: 'center center',
                opacity: 0,
                transition: 'transform 0.45s ease, opacity 0.35s ease',
                pointerEvents: 'none'
              }}
              image={hoverImage}
              alt=""
              aria-hidden
            />
          )}
        </Box>

        <CardContent
          sx={{
            position: 'absolute',
            bottom: { xs: -4, sm: 0, md: 0 },
            left: 0,
            right: 0,
            zIndex: 2,
            color: 'white',
            pt: 6
          }}
        >
          <CardOverlayPills
            name={productName}
            pricing={pricing}
            onPriceClick={handleLearnMoreClick}
            isHebrew={isHebrew}
          />
        </CardContent>
      </Card>

      {/* Snake sits on the exact card edge (sibling, not clipped by Card overflow) */}
      {size.w > 0 && size.h > 0 && (
        <Box
          component="svg"
          aria-hidden
          width={size.w}
          height={size.h}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: size.w,
            height: size.h,
            zIndex: 6,
            pointerEvents: 'none',
            overflow: 'visible',
            transform: isHebrew ? 'scaleX(-1)' : 'none'
          }}
        >
          <rect
            className="product-snake-path"
            x={inset}
            y={inset}
            width={rectW}
            height={rectH}
            rx={rectRx}
            ry={rectRx}
            pathLength={1}
            fill="none"
            stroke={SNAKE_COLOR}
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="butt"
            strokeLinejoin="round"
          />
        </Box>
      )}
    </Box>
  );
}
